import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Stack, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';

import { activeDirectorySchema } from '../../../components/v2/division/form/schema/activeDirectorySchema';
import AutoCompleteInput from 'components/v2/input/AutoCompleteInput';
import { useLocalization } from 'localization';
import ClickableImage from 'components/v2/image/ClickableImage';
import { useAuth } from 'modules/auth/hooks';
import { useDebounce } from 'hooks/general/useDebounce';
import { DEBOUNCE_TIME } from 'constants/server/apiSettings';
import { BRANCH_SEARCH_ENDPOINT } from 'constants/server/apiEndPoint';
import { DEFAULT_TABLE_PAGE_SIZE, LAYOUT_SPACING } from 'constants/ui/style';
import { authenticateUserMutation, getUsersByBranchIdApi } from 'api/users/query';
import { EFKT_SITE_URL, LAST_DEPARTMENT_ID } from 'api/http';

const ActiveDirectory = () => {
  const schema = activeDirectorySchema();
  const { t } = useLocalization();
  const { logout } = useAuth();
  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      customer: '',
      departmentName: '',
      branchId: '',
      selectedCustomerId: '',
    },
    mode: 'onChange',
  });

  const { control, formState, watch, getValues, setValue, handleSubmit } = methods;

  const departmentValue = watch('departmentName');
  const branchId = watch('branchId');

  const debouncedDepartmentName = useDebounce(departmentValue, DEBOUNCE_TIME);

  const { data, isLoading: isUserLoading } = getUsersByBranchIdApi(branchId);
  const { mutateAsync, isLoading } = authenticateUserMutation();
  const onSubmit = async () => {
    //store branchId to bind the chosen department
    //in the create order widget
    localStorage.setItem(LAST_DEPARTMENT_ID, branchId);
    await mutateAsync(getValues('selectedCustomerId'));
  };

  return (
    <Stack height="100%" sx={{ padding: '2.5rem' }}>
      <Stack>
        <Button
          type="submit"
          sx={{ alignSelf: 'flex-start' }}
          variant="outlined"
          startIcon={<KeyboardBackspaceIcon />}
          onClick={() => open(EFKT_SITE_URL, '_self')}
        >
          {t('common.terms.backButton')}
        </Button>
      </Stack>
      <Stack
        gap={3}
        sx={{
          width: { xs: '100%', lg: '50%' },
          minWidth: { xs: 'unset', lg: '26rem' },
          mx: 'auto',
          my: 'auto',
        }}
      >
        <Stack mb={2} width="100%">
          <ClickableImage path="" imgSrc="/logo.png" width="4.44rem" height="1.5rem" />
        </Stack>
        <Stack mb={2} gap={2} width="100%">
          <Typography variant="h3">{t('auth.pages.activeDirectory.title')}</Typography>
          <Typography variant="subtitle1">{t('auth.pages.activeDirectory.instructions')}</Typography>
        </Stack>
        <Stack width="100%" spacing={LAYOUT_SPACING.FORM.xs}>
          <AutoCompleteInput
            control={control}
            otherError={formState.errors?.branchId}
            placeholder={t('common.placeholders.minChars', { min: 3 })}
            endpoint={BRANCH_SEARCH_ENDPOINT}
            payload={{
              searchString: debouncedDepartmentName,
              skip: 0,
              take: DEFAULT_TABLE_PAGE_SIZE,
            }}
            required
            searchValue={debouncedDepartmentName}
            pickedField={{ label: 'name', value: 'id' }}
            transformLabel={option => `${option.name} ${option.axCustomerId}`}
            onInputChange={(_, value) => {
              setValue('departmentName', value);
              setValue('branchId', '');
              setValue('customer', '');
              setValue('selectedCustomerId', '');
            }}
            onChange={(_, option) => {
              if (option) setValue('branchId', option.value);
            }}
            queryKey="searchBranch"
            name="departmentName"
            label="common.terms.department"
          />
          <AutoCompleteInput
            otherError={formState.errors?.selectedCustomerId}
            required
            options={
              data?.map(option => ({
                label: `${option.firstName} ${option.lastName} (${option.email})`,
                value: option.id,
              })) ?? []
            }
            onChange={(_, option) => {
              if (option) {
                setValue('customer', option.label);
                setValue('selectedCustomerId', option.value);
              }
            }}
            noOptionsText="common.messages.noUsersFound"
            placeholder={t('common.placeholders.selectUser')}
            onInputChange={(_, value) => setValue('customer', value)}
            control={control}
            isLoading={isUserLoading}
            name="customer"
            label="common.terms.user"
          />
        </Stack>
        <Stack width="100%">
          <Button
            onClick={handleSubmit(onSubmit)}
            sx={{
              alignSelf: 'flex-end',
            }}
            variant="contained"
            endIcon={<ArrowRightAltIcon />}
            loading={isLoading}
          >
            {t('common.terms.continue')}
          </Button>
        </Stack>
      </Stack>
      <Stack width="100%" justifyContent="center" alignContent="center">
        <Button
          onClick={() => logout(true)}
          sx={{
            color: '#C62B66',
            width: '100%',
            backgroundColor: 'transparent',
          }}
          variant="text"
        >
          {t('auth.pages.activeDirectory.logout')}
        </Button>
      </Stack>
    </Stack>
  );
};

export default ActiveDirectory;
